import { defineStore } from "pinia";
import { DEFAULT_HOME_TREASURY } from "@/preferences/types";
import { groupKeyForCurso } from "@/preferences/groupKey";
import { emptyUserPreferences, loadGroupSettings, loadUserPreferences, saveGroupSettings, saveUserPreferences, } from "@/preferences/preferencesStorage";
import { cursoRefFromGuardian, fetchGroupModuleSettings, hasLocalGroupCustomizations, saveGroupModuleSettingsRemote, toLocalGroupSettings, } from "@/services/groupModuleSettingsService";
const loadedGroupKeys = new Set();
const inflightLoads = new Map();
const putChains = new Map();
const pendingPuts = new Map();
const enqueuePut = (groupKey, task) => {
    pendingPuts.set(groupKey, (pendingPuts.get(groupKey) ?? 0) + 1);
    const next = (putChains.get(groupKey) ?? Promise.resolve())
        .catch(() => undefined)
        .then(task)
        .finally(() => {
        const left = (pendingPuts.get(groupKey) ?? 1) - 1;
        if (left <= 0)
            pendingPuts.delete(groupKey);
        else
            pendingPuts.set(groupKey, left);
    });
    putChains.set(groupKey, next);
    return next;
};
export const useUiPreferencesStore = defineStore("uiPreferences", {
    state: () => ({
        revision: 0,
    }),
    actions: {
        userPrefs(email) {
            void this.revision;
            return loadUserPreferences(email);
        },
        groupSettings(groupKey) {
            void this.revision;
            return loadGroupSettings(groupKey);
        },
        pinnedModules(email, groupKey) {
            const prefs = this.userPrefs(email);
            if (!prefs)
                return null;
            const group = prefs.groups[groupKey];
            if (!group)
                return null;
            return group.pinnedModules;
        },
        setPinnedModules(email, groupKey, pinnedModules) {
            const current = this.userPrefs(email) ?? emptyUserPreferences();
            current.groups = {
                ...current.groups,
                [groupKey]: { pinnedModules: [...pinnedModules] },
            };
            saveUserPreferences(email, current);
            this.revision += 1;
        },
        togglePinnedModule(email, groupKey, moduleId, nextVisible, fallbackPinned) {
            const current = this.pinnedModules(email, groupKey);
            const base = current ? [...current] : [...fallbackPinned];
            const without = base.filter((id) => id !== moduleId);
            const pinned = nextVisible ? [...without, moduleId] : without;
            this.setPinnedModules(email, groupKey, pinned);
        },
        homePrefs(email) {
            void this.revision;
            return this.userPrefs(email)?.home ?? null;
        },
        setHomePreferences(email, home) {
            const current = this.userPrefs(email) ?? emptyUserPreferences();
            saveUserPreferences(email, {
                ...current,
                groups: { ...current.groups },
                home: {
                    widgets: home.widgets.map((item) => ({ ...item })),
                    treasury: home.treasury ? { ...home.treasury } : home.treasury,
                },
            });
            this.revision += 1;
        },
        patchHomePreferences(email, fallback, updater) {
            const current = loadUserPreferences(email) ?? emptyUserPreferences();
            const base = current.home
                ? {
                    widgets: current.home.widgets.map((item) => ({ ...item })),
                    treasury: {
                        ...DEFAULT_HOME_TREASURY,
                        ...current.home.treasury,
                    },
                }
                : {
                    widgets: fallback.widgets.map((item) => ({ ...item })),
                    treasury: {
                        ...DEFAULT_HOME_TREASURY,
                        ...fallback.treasury,
                    },
                };
            this.setHomePreferences(email, updater(base));
        },
        setGroupModuleEnabled(groupKey, moduleId, enabled, curso) {
            const settings = loadGroupSettings(groupKey);
            if (moduleId === "treasury") {
                settings.treasury = {
                    ...settings.treasury,
                    enabled,
                };
            }
            else if (moduleId === "bank-statements") {
                if (!enabled || settings.treasury.enabled !== false) {
                    settings.treasury = {
                        ...settings.treasury,
                        bankStatements: enabled,
                    };
                }
            }
            else {
                settings.modules = {
                    ...settings.modules,
                    [moduleId]: enabled,
                };
            }
            saveGroupSettings(settings);
            this.revision += 1;
            const ref = cursoRefFromGuardian(curso);
            if (!ref || !groupKey)
                return;
            void enqueuePut(groupKey, async () => {
                const latest = loadGroupSettings(groupKey);
                try {
                    const remote = await saveGroupModuleSettingsRemote(ref, latest);
                    saveGroupSettings(toLocalGroupSettings(groupKey, remote));
                    this.revision += 1;
                }
                catch (error) {
                    console.error("[groupModuleSettings] No se pudo guardar en el servidor", error);
                }
            });
        },
        async ensureGroupSettingsLoaded(curso) {
            const groupKey = groupKeyForCurso(curso);
            const ref = cursoRefFromGuardian(curso);
            if (!groupKey || !ref)
                return;
            if (loadedGroupKeys.has(groupKey))
                return;
            const inflight = inflightLoads.get(groupKey);
            if (inflight) {
                await inflight;
                return;
            }
            const load = (async () => {
                try {
                    const remote = await fetchGroupModuleSettings(ref);
                    if ((pendingPuts.get(groupKey) ?? 0) > 0) {
                        loadedGroupKeys.add(groupKey);
                        return;
                    }
                    const local = loadGroupSettings(groupKey);
                    if (!remote.updated_at && hasLocalGroupCustomizations(local)) {
                        try {
                            const saved = await saveGroupModuleSettingsRemote(ref, local);
                            saveGroupSettings(toLocalGroupSettings(groupKey, saved));
                            this.revision += 1;
                            loadedGroupKeys.add(groupKey);
                            return;
                        }
                        catch {
                            /* si no puede escribir, usa lo remoto (defaults institucionales) */
                        }
                    }
                    saveGroupSettings(toLocalGroupSettings(groupKey, remote));
                    this.revision += 1;
                    loadedGroupKeys.add(groupKey);
                }
                catch (error) {
                    console.error("[groupModuleSettings] No se pudo leer del servidor", error);
                }
            })();
            inflightLoads.set(groupKey, load);
            try {
                await load;
            }
            finally {
                inflightLoads.delete(groupKey);
            }
        },
    },
});
